/**
 * BookTemplates (iter-103) — Book Format Templates library.
 *
 * A Book Template is just a book with `is_template: true`. This page
 * lists the caller's templates, offers "+ New template" (which spawns
 * an empty template and drops the author into the standard book
 * editor), and per-row delete. Editing a template happens in the SAME
 * book editor as regular books — no separate UI to maintain.
 *
 * The "spawn a real book from this template" flow lives on the
 * Library dashboard's New-book menu (see `Dashboard.jsx`).
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { WorkspacePickerButton } from "@/pages/Dashboard";

export default function BookTemplates() {
  const nav = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, wRes] = await Promise.all([
        api.get("/book-templates"),
        // iter-107 — pull the caller's workspaces so the row-level
        // Share picker knows what checkboxes to render. Failure is
        // non-fatal (templates still list, picker just shows an
        // empty state).
        api.get("/workspaces/mine").catch(() => ({ data: [] })),
      ]);
      setTemplates(tRes.data || []);
      setWorkspaces((wRes.data || []).map((w) => ({ id: w.id, name: w.name })));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createTemplate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/books", {
        title: "New Book Template",
        author: "Template",
        is_template: true,
      });
      toast.success("Template created");
      // Drop the author straight into the same editor they use for
      // regular books — Manual Compose, presets, kits, design controls
      // all work identically because a template IS a book.
      nav(`/book/${data.id}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete template “${title || "Untitled"}”? This cannot be undone.`)) return;
    try {
      await api.delete(`/books/${id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      toast.success("Template deleted");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // iter-107 — persist workspace_ids for a template. Same endpoint
  // as regular books; templates just carry `is_template: true`. On
  // success we update the local row so the button label reflects
  // the new count without a full reload.
  const saveWorkspaces = async (templateId, workspaceIds) => {
    try {
      await api.patch(`/books/${templateId}`, { workspace_ids: workspaceIds });
      setTemplates((prev) => prev.map((t) =>
        t.id === templateId ? { ...t, workspace_ids: workspaceIds } : t
      ));
      toast.success(workspaceIds.length === 0
        ? "Template moved to Personal"
        : `Template shared with ${workspaceIds.length} workspace${workspaceIds.length === 1 ? "" : "s"}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="min-h-screen bg-[color:var(--canvas)]">
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)]">§ Library</p>
            <h1 className="font-editorial text-5xl leading-tight mt-2" data-testid="book-templates-title">
              Book Templates
            </h1>
            <p className="text-sm text-[color:var(--text-muted)] mt-2 max-w-2xl">
              Reusable book skeletons — the exact same Manual Entry UI, saved empty. Add all your standard front matter, back matter, appendices and chapter placeholders once, then spawn a fresh book from any template with one click.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/dashboard" className="btn-ghost inline-flex items-center gap-2 text-sm" data-testid="book-templates-back">
              <ArrowLeft className="w-4 h-4"/> Dashboard
            </Link>
            <button onClick={createTemplate} disabled={busy}
                    className="btn-primary inline-flex items-center gap-2 text-sm"
                    data-testid="book-templates-new">
              <Plus className="w-4 h-4"/> {busy ? "Creating…" : "New template"}
            </button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-[color:var(--text-muted)]">Loading…</p>
        ) : templates.length === 0 ? (
          <div className="border border-dashed border-[color:var(--border)] p-16 text-center"
               data-testid="book-templates-empty">
            <p className="font-editorial text-2xl mb-2">No templates yet.</p>
            <p className="text-sm text-[color:var(--text-muted)] mb-4">
              Create your first Book Template to speed up every future book.
            </p>
            <button onClick={createTemplate} disabled={busy}
                    className="btn-primary inline-flex items-center gap-2 text-sm">
              <Plus className="w-4 h-4"/> New template
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-[color:var(--border)] border border-[color:var(--border)] bg-white"
              data-testid="book-templates-list">
            {templates.map((t) => (
              <li key={t.id} className="flex items-center gap-3 px-4 py-3"
                  data-testid={`book-template-row-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <Link to={`/book/${t.id}`}
                        className="font-medium hover:underline"
                        data-testid={`book-template-${t.id}-open`}>
                    {t.title || "Untitled template"}
                  </Link>
                  <p className="text-[10px] text-[color:var(--text-muted)] mt-0.5">
                    {(t.chapters?.length || 0)} chapters
                    {t.author ? ` · ${t.author}` : ""}
                    {t.updated_at ? ` · updated ${new Date(t.updated_at).toLocaleDateString()}` : ""}
                  </p>
                </div>
                {/* iter-107 — Multi-workspace share picker per template
                    row. Same UX as the Library dashboard. Hidden
                    when the author has no workspaces (nothing to
                    share to). */}
                {workspaces.length > 0 && (
                  <div className="w-48 shrink-0" data-testid={`book-template-${t.id}-share`}>
                    <WorkspacePickerButton
                      bookId={t.id}
                      itemLabel="template"
                      currentIds={t.workspace_ids || []}
                      workspaces={workspaces}
                      onSave={(ids) => saveWorkspaces(t.id, ids)}
                    />
                  </div>
                )}
                <Link to={`/book/${t.id}`}
                      className="btn-ghost text-xs"
                      data-testid={`book-template-${t.id}-edit`}>
                  Edit
                </Link>
                <button onClick={()=>remove(t.id, t.title)}
                        className="text-[color:var(--text-muted)] hover:text-red-700 p-1"
                        title="Delete template"
                        data-testid={`book-template-${t.id}-delete`}>
                  <Trash2 className="w-4 h-4"/>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
